import { PropertyFilterProps } from '@amzn/awsui-components-console/property-filter';
import { EQUAL_COMPARISION } from 'src/constants/ComponentConstants';
import { WorkflowDefinition } from 'src/@types/selfServeTypes/workflowDefinition.interface';

export const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
	{
		key: 'workflowDefinitionName',
		operators: EQUAL_COMPARISION,
		propertyLabel: 'Workflow Name',
		groupValuesLabel: 'Workflow Name values',
	},
	{
		key: 'entityType',
		operators: EQUAL_COMPARISION,
		propertyLabel: 'Entity Type',
		groupValuesLabel: 'Entity Type values',
	},
	{
		key: 'workflowIntention',
		operators: EQUAL_COMPARISION,
		propertyLabel: 'Workflow Intention',
		groupValuesLabel: 'Workflow Intention values',
	},
	{
		key: 'createdYear',
		operators: EQUAL_COMPARISION,
		propertyLabel: 'Created Year',
		groupValuesLabel: 'Created Year values',
	},
	{
		key: 'createdBy',
		operators: EQUAL_COMPARISION,
		propertyLabel: 'Created By',
		groupValuesLabel: 'Created By values',
	},
	{
		key: 'status',
		operators: EQUAL_COMPARISION,
		propertyLabel: 'Status',
		groupValuesLabel: 'Status values',
	},
	{
		key: 'isReRunAllowed',
		operators: EQUAL_COMPARISION,
		propertyLabel: 'Re-Run Allowed',
		groupValuesLabel: 'Re-Run Allowed values',
	},
	{
		key: 'usableAsSubWorkflow',
		operators: EQUAL_COMPARISION,
		propertyLabel: 'Can Be Used as Sub-workflow',
		groupValuesLabel: 'Can Be Used as Sub-workflow values',
	},
	{
		key: 'lastUpdatedBy',
		operators: EQUAL_COMPARISION,
		propertyLabel: 'Last Updated By',
		groupValuesLabel: 'Last Updated By values',
	},
];

export function getFilteringOptions(
	workflows: WorkflowDefinition[],
	favoriteWorkflowIds: string[] = [],
	currentUserName?: string
): PropertyFilterProps.FilteringOption[] {
	const favoriteSet = new Set(favoriteWorkflowIds);
	const workflowNames = [
		...new Set(workflows.map((w) => w.workflowDefinitionName).filter(Boolean)),
	].sort();
	const sortedWorkflowNames = [
		...workflowNames.filter((name) =>
			workflows.some(
				(w) =>
					w.workflowDefinitionName === name && favoriteSet.has(w.workflowId)
			)
		),
		...workflowNames.filter(
			(name) =>
				!workflows.some(
					(w) =>
						w.workflowDefinitionName === name && favoriteSet.has(w.workflowId)
				)
		),
	];
	const entityTypes = [
		...new Set(workflows.map((w) => w.entityType?.toLowerCase()).filter(Boolean)),
	].sort();
	const intentions = [
		...new Set(
			workflows
				.map((w) => w.workflowIntention)
				.filter((v): v is string => Boolean(v))
		),
	].sort();
	const years = [
		...new Set(
			workflows
				.map((w) =>
					w.createdAt
						? new Date(Number(w.createdAt) * 1000).getFullYear().toString()
						: null
				)
				.filter(Boolean) as string[]
		),
	]
		.sort()
		.reverse();
	const createdByUsers = [
		...new Set(workflows.map((w) => w.createdBy).filter(Boolean)),
	].sort((a, b) => {
		if (a === currentUserName) return -1;
		if (b === currentUserName) return 1;

		return a.localeCompare(b);
	});
	const lastUpdatedByUsers = [
		...new Set(workflows.map((w) => w.lastUpdatedBy).filter(Boolean)),
	].sort((a, b) => {
		if (a === currentUserName) return -1;
		if (b === currentUserName) return 1;

		return a.localeCompare(b);
	});
	const statuses = [
		...new Set(workflows.map((w) => w.status).filter(Boolean)),
	].sort();

	return [
		...sortedWorkflowNames.map((v) => {
			const isFavorite = workflows.some(
				(w) => w.workflowDefinitionName === v && favoriteSet.has(w.workflowId)
			);

			return {
				propertyKey: 'workflowDefinitionName',
				value: v,
				label: isFavorite ? `★ ${v}` : v,
			};
		}),
		...entityTypes.map((v) => ({ propertyKey: 'entityType', value: v })),
		...intentions.map((v) => ({ propertyKey: 'workflowIntention', value: v })),
		...years.map((v) => ({ propertyKey: 'createdYear', value: v })),
		...createdByUsers.map((v) => ({ propertyKey: 'createdBy', value: v })),
		...statuses.map((v) => ({ propertyKey: 'status', value: v })),
		...(workflows.length > 0
			? [
					{ propertyKey: 'isReRunAllowed', value: 'Yes' },
					{ propertyKey: 'isReRunAllowed', value: 'No' },
					{ propertyKey: 'usableAsSubWorkflow', value: 'Yes' },
					{ propertyKey: 'usableAsSubWorkflow', value: 'No' },
			  ]
			: []),
		...lastUpdatedByUsers.map((v) => ({
			propertyKey: 'lastUpdatedBy',
			value: v,
		})),
	];
}

export function filterWorkflows(
	workflows: WorkflowDefinition[],
	query: PropertyFilterProps.Query
): WorkflowDefinition[] {
	if (query.tokens.length === 0) {
		return workflows;
	}

	return workflows.filter((item) => {
		return query.tokens.every((token) => {
			const { propertyKey, value, operator } =
				token as PropertyFilterProps.Token;

			if (!propertyKey) {
				const searchText = value?.toLowerCase() || '';

				return Object.values(item).some(
					(v) => v != null && String(v).toLowerCase().includes(searchText)
				);
			}

			let itemValue: string | null = null;

			if (propertyKey === 'createdYear') {
				itemValue = item.createdAt
					? new Date(Number(item.createdAt) * 1000).getFullYear().toString()
					: null;
			} else if (propertyKey === 'isReRunAllowed') {
				itemValue = item.isReRunAllowed ? 'Yes' : 'No';
			} else if (propertyKey === 'usableAsSubWorkflow') {
				itemValue = item.usableAsSubWorkflow ? 'Yes' : 'No';
			} else if (propertyKey === 'entityType') {
				itemValue = item.entityType?.toLowerCase() ?? null;
			} else {
				itemValue =
					(item as unknown as Record<string, string | undefined>)[
						propertyKey
					] ?? null;
			}

			if (operator === '=') {
				return itemValue === value;
			}

			if (operator === '!=') {
				return itemValue !== value;
			}

			return true;
		});
	});
}
